// Full-screen mode for the main map (/map).
//
// We deliberately fullscreen the *document*, not the map container: every dialog on the map page
// (Maps, Options, Go To, Waypoint, Loading) is teleported to <body>, and Bootstrap appends its modal
// backdrops there too. Fullscreening only the map subtree would make all of them invisible.
//
// The visible effect comes from a body class (`map-fullscreen`) that fixes the map container to the
// viewport and hides the navbar. The browser Fullscreen API is used on top when available, and is
// silently skipped where it isn't (iPhone Safari), so the class alone gives a usable pseudo-fullscreen.

export const MAP_FULLSCREEN_CLASS = 'map-fullscreen';

/* Minimal surface of `document` we touch, so tests can pass a stub. */
export interface FullscreenDocument {
  body: { classList: DOMTokenList } | HTMLElement;
  documentElement: { requestFullscreen?: () => Promise<void> } | HTMLElement;
  fullscreenElement?: Element | null;
  fullscreenEnabled?: boolean;
  exitFullscreen?: () => Promise<void>;
}

export function isFullscreenApiAvailable(doc: FullscreenDocument): boolean {
  return doc.fullscreenEnabled !== false
    && typeof doc.documentElement.requestFullscreen === 'function'
    && typeof doc.exitFullscreen === 'function';
}

export function isMapFullscreen(doc: FullscreenDocument): boolean {
  return doc.body.classList.contains(MAP_FULLSCREEN_CLASS);
}

/** Enter full-screen mode. Idempotent. Always applies the class; uses the API when it exists. */
export function enterMapFullscreen(doc: FullscreenDocument): void {
  doc.body.classList.add(MAP_FULLSCREEN_CLASS);
  if (isFullscreenApiAvailable(doc) && !doc.fullscreenElement) {
    // A denied request (e.g. not a user gesture) must not surface as an unhandled rejection.
    doc.documentElement.requestFullscreen!().catch(() => {});
  }
}

/** Leave full-screen mode. Idempotent. Only calls exitFullscreen if something is actually fullscreen. */
export function exitMapFullscreen(doc: FullscreenDocument): void {
  doc.body.classList.remove(MAP_FULLSCREEN_CLASS);
  if (doc.fullscreenElement && typeof doc.exitFullscreen === 'function') {
    doc.exitFullscreen().catch(() => {});
  }
}

/** Flip state and return the new "is fullscreen" value. */
export function toggleMapFullscreen(doc: FullscreenDocument): boolean {
  if (isMapFullscreen(doc)) {
    exitMapFullscreen(doc);
    return false;
  }
  enterMapFullscreen(doc);
  return true;
}

/**
 * The browser can leave fullscreen without us (Esc key, browser UI, tab switch on some platforms).
 * Call this from a `fullscreenchange` listener: when we're in map-fullscreen mode but the document
 * is no longer fullscreen, drop the class so the page and the button agree. Returns true if it
 * exited. When the API is unavailable there is nothing to sync, so it never exits.
 */
export function syncMapFullscreenWithDocument(doc: FullscreenDocument): boolean {
  if (!isFullscreenApiAvailable(doc)) return false;
  if (isMapFullscreen(doc) && !doc.fullscreenElement) {
    doc.body.classList.remove(MAP_FULLSCREEN_CLASS);
    return true;
  }
  return false;
}

/**
 * In pseudo-fullscreen the browser won't handle Esc for us. That covers both "no API" (iPhone
 * Safari) and "API present but requestFullscreen was denied": in either case nothing is actually
 * fullscreen, so `fullscreenElement` is null. Returns true when a keydown should exit.
 */
export function shouldExitOnKeydown(doc: FullscreenDocument, key: string): boolean {
  return key === 'Escape' && isMapFullscreen(doc) && !doc.fullscreenElement;
}

/** Icon + label for the toolbar button, derived from state so the two can't drift apart. */
export function fullscreenButtonPresentation(active: boolean): { icon: string; label: string } {
  // open_in_full / close_fullscreen rather than fullscreen / fullscreen_exit: the latter's corner
  // brackets look identical to the crop_free icon on the neighbouring "Fit map to bounds" button.
  return active
    ? { icon: 'close_fullscreen', label: 'Exit full screen' }
    : { icon: 'open_in_full', label: 'Full screen' };
}
